import Checkbox from '@mui/material/Checkbox';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress } from '@mui/material';

import constants from '../../constants';
import { updateTagGlobally, bulkUpdateObjectTags, deleteTagGlobally, createTag } from '../../store/Objects';

const TagsManager = ({ objectType, onTagSelect, selectedTags = [], selectedObjects = [], onTagsApplied, onTagsChange, onClose, mode = 'manage', skipRefresh = false, allRecordsSelected = false, totalCount = 0, viewId = null, filters = null }) => {
  const dispatch = useDispatch();

  // Get tags from Redux store based on object type
  const schema = constants.getObjectSchema(objectType);
  const stateKey = schema?.type || objectType;
  const { deals, contacts, companies, agents, workflows } = useSelector(state => state['Objects']);
  const objectState = { deals, contacts, companies, agents, workflows }[stateKey];
  const allTags = objectState?.allTags || [];

  // When "Select all N records" mode is active, actions apply to every record matching the current view/filters
  const effectiveCount = allRecordsSelected ? totalCount : selectedObjects.length;
  const selectAllPayload = allRecordsSelected ? { updateAll: true, viewId, filters } : {};

  // State management
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#EF4444');

  const [editingTagName, setEditingTagName] = useState('');
  const [editingTagColor, setEditingTagColor] = useState('#EF4444');

  const [searchText, setSearchText] = useState('');
  const [tagsToApply, setTagsToApply] = useState(() => {
    if ((mode === 'select' || mode === 'form') && Array.isArray(selectedTags) && selectedTags.length > 0) return [...selectedTags];
    return [];
  });
  const originalTags = useRef(selectedTags);
  const searchInputRef = useRef(null);
  const [isApplying, setIsApplying] = useState(false);
  const [filterTags, setFilterTags] = useState(() => {
    if (mode === 'filter' && Array.isArray(selectedTags)) return selectedTags.reduce((acc, tag) => ({ ...acc, [tag]: true }), {});
    return {};
  });

  // Color palette
  const colorPalette = [
    '#EC4899', '#F472B6', '#F87171', '#FB923C', '#FBBF24',
    '#FDE047', '#A3E635', '#FF0000', '#6EE7B7', '#5EEAD4',
    '#22D3EE', '#60A5FA', '#818CF8', '#A78BFA', '#C084FC',
    '#E879F9', '#FB7185', '#FCA5A5', '#FDBA74', '#FCD34D',
    '#BEF264', '#86EFAC', '#67E8F9', '#93C5FD', '#C4B5FD'
  ];

  // Helper functions for backward compatibility with string and object tags
  const getTagName = (tag) => {
    return typeof tag === 'string' ? tag : tag?.name || '';
  };

  const getTagColor = (tag) => {
    return typeof tag === 'string' ? '#6B7280' : tag?.color || '#6B7280';
  };

  const createTagObject = (tag, defaultColor = '#6B7280') => {
    if (typeof tag === 'string') return { name: tag, color: defaultColor };
    return tag;
  };

  // Initialize filter tags only once from props (for filter mode)
  const isInitialized = useRef(false);

  useEffect(() => {
    if (mode === 'filter' && Array.isArray(selectedTags) && !isInitialized.current) {
      setFilterTags(selectedTags.reduce((acc, tag) => ({ ...acc, [getTagName(tag)]: true }), {}));
      isInitialized.current = true;
    }
  }, [mode]);

  useEffect(() => {
    if (!createModalOpen && !editModalOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [createModalOpen, editModalOpen]);

  useEffect(() => {
    if (!onClose) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle menu open
  const handleMenuClick = (event, tag) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTag(tag);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTag(null);
  };

  // Handle create tag
  const handleCreateTag = () => {
    setNewTagName(searchText.trim());
    setSelectedColor('#EF4444');
    setCreateModalOpen(true);
    handleMenuClose();
  };

  // Handle save new tag
  const handleSaveNewTag = () => {
    if (newTagName.trim()) {
      const newTag = { name: newTagName.trim(), color: selectedColor };
      setIsApplying(true);
      dispatch(createTag({
        objectType, tagName: newTag.name, tagColor: newTag.color,
        successCallback: () => {
          setIsApplying(false);
          setCreateModalOpen(false);
          setNewTagName('');
          setSelectedColor('#EF4444');

          if (mode === 'form') {
            const updatedTags = [...tagsToApply, newTag];
            setTagsToApply(updatedTags);
            if (onTagsChange) onTagsChange(updatedTags.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
          } else if (mode === 'select' && selectedObjects.length > 0) handleTagSelection(newTag);
          else {
            if (mode !== 'filter' && onTagSelect) onTagSelect(newTag);
            if (onTagsApplied) onTagsApplied();
          }
        },
        failCallback: () => setIsApplying(false)
      }));
    }
  };

  // Handle edit tag
  const handleEditTag = () => {
    const tagName = getTagName(selectedTag);
    const tagColor = getTagColor(selectedTag);
    setEditingTagName(tagName);
    setEditingTagColor(tagColor);
    setEditModalOpen(true);
    setAnchorEl(null);
  };

  // Handle update tag
  const handleUpdateTag = () => {
    if (editingTagName.trim() && selectedTag) {
      const oldTagName = getTagName(selectedTag);
      setIsApplying(true);
      dispatch(updateTagGlobally({
        objectType, oldTagName, newTagName: editingTagName.trim(), newTagColor: editingTagColor,
        successCallback: () => {
          setIsApplying(false);
          setEditModalOpen(false);
          // Update form mode selection if edited tag was selected
          if (mode === 'form' && selectedTag) {
            const oldName = getTagName(selectedTag);
            const updatedTags = tagsToApply.map(t =>
              getTagName(t) === oldName ? { name: editingTagName.trim(), color: editingTagColor } : t
            );
            setTagsToApply(updatedTags);
            if (onTagsChange) onTagsChange(updatedTags.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
          }
          setEditingTagName('');
          setEditingTagColor('#EF4444');
          setSelectedTag(null);

          if (onTagsApplied) onTagsApplied();
        },
        failCallback: () => setIsApplying(false)
      }));
    }
  };

  // Handle delete tag
  const handleDeleteTag = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null);
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    if (selectedTag) {
      const tagName = getTagName(selectedTag);
      if (mode === 'select' && selectedObjects && selectedObjects.length > 0) {
        setIsApplying(true);
        dispatch(bulkUpdateObjectTags({
          objectType,
          tagsToAdd: [],
          objectIds: selectedObjects,
          ...selectAllPayload,
          tagsToRemove: [{ name: tagName, color: getTagColor(selectedTag) }],
          successCallback: () => {
            setIsApplying(false);
            setSelectedTag(null);
            setDeleteConfirmOpen(false);
  
            if (onTagsApplied) onTagsApplied();
          }, failCallback: () => setIsApplying(false)
        }))
      } else if (mode === 'filter' || mode === 'form') {
        setIsApplying(true);
        dispatch(deleteTagGlobally({
          objectType, tagName,
          successCallback: () => {
            setIsApplying(false);
            setSelectedTag(null);
            setDeleteConfirmOpen(false);
            // For form mode, remove deleted tag from selection
            if (mode === 'form') {
              const updatedTags = tagsToApply.filter(t => getTagName(t) !== tagName);
              setTagsToApply(updatedTags);
              if (onTagsChange) onTagsChange(updatedTags.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
            }
            if (onTagsApplied) onTagsApplied();
          }, failCallback: () => setIsApplying(false)
        }))
      } else setDeleteConfirmOpen(false)
    }
  }

  // Handle tag selection in selection mode - immediately apply/remove
  const handleTagSelection = (tag) => {
    if (mode === 'form') {
      const tagName = getTagName(tag);
      const isSelected = tagsToApply.some(t => getTagName(t) === tagName);
      const updatedTags = isSelected
        ? tagsToApply.filter(t => getTagName(t) !== tagName)
        : [...tagsToApply, createTagObject(tag, getTagColor(tag))];
      setTagsToApply(updatedTags);
      if (onTagsChange) onTagsChange(updatedTags.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
      return;
    }
    if (mode !== 'select' || selectedObjects.length === 0) return;
    // Reason: block further toggles while a bulk update is in flight — select-all can touch thousands of records
    if (isApplying) return;
    const tagName = getTagName(tag);
    const isSelected = tagsToApply.some(t => getTagName(t) === tagName);

    if (isSelected) {
      const updatedTags = tagsToApply.filter(t => getTagName(t) !== tagName);
      setTagsToApply(updatedTags);
      // Optimistic local update for parent
      if (onTagsChange) onTagsChange(updatedTags.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
      if (skipRefresh && onTagsApplied) onTagsApplied(updatedTags);
      setIsApplying(true);
      dispatch(bulkUpdateObjectTags({
        objectType, objectIds: selectedObjects, tagsToAdd: [], tagsToRemove: [tag], skipRefresh, ...selectAllPayload,
        successCallback: () => { setIsApplying(false); if (!skipRefresh && onTagsApplied) onTagsApplied(updatedTags); },
        failCallback: () => {
          setIsApplying(false);
          setTagsToApply(prev => [...prev, tag]);
          // Revert optimistic update
          if (onTagsChange) onTagsChange([...tagsToApply].map(t => ({ name: getTagName(t), color: getTagColor(t) })));
          if (skipRefresh && onTagsApplied) onTagsApplied([...tagsToApply]);
        }
      }));
    } else {
      const updatedTags = [...tagsToApply, tag];
      setTagsToApply(updatedTags);
      // Optimistic local update for parent
      if (onTagsChange) onTagsChange(updatedTags.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
      if (skipRefresh && onTagsApplied) onTagsApplied(updatedTags);
      setIsApplying(true);
      dispatch(bulkUpdateObjectTags({
        objectType, objectIds: selectedObjects, tagsToAdd: [tag], tagsToRemove: [], skipRefresh, ...selectAllPayload,
        successCallback: () => { setIsApplying(false); if (!skipRefresh && onTagsApplied) onTagsApplied(updatedTags); },
        failCallback: () => {
          setIsApplying(false);
          setTagsToApply(prev => prev.filter(t => getTagName(t) !== tagName));
          // Revert optimistic update
          if (onTagsChange) onTagsChange(tagsToApply.map(t => ({ name: getTagName(t), color: getTagColor(t) })));
          if (skipRefresh && onTagsApplied) onTagsApplied(tagsToApply);
        }
      }));
    }
  };

  // Handle tag filter selection (for filter mode)
  const handleFilterTagToggle = (tag) => {
    const tagName = getTagName(tag);
    const newFilterTags = { ...filterTags };
    if (newFilterTags[tagName]) delete newFilterTags[tagName];
    else newFilterTags[tagName] = true;
    setFilterTags(newFilterTags);

    if (onTagsChange) {
      const selectedTagNames = Object.keys(newFilterTags).filter(key => newFilterTags[key]);
      onTagsChange(selectedTagNames);
    }
  };

  // Handle applying selected tags to selected objects
  const handleApplySelectedTags = () => {
    if (mode === 'select' && selectedObjects.length > 0) {
      const origNames = originalTags.current.map(t => getTagName(t));
      const currNames = tagsToApply.map(t => getTagName(t));

      const tagsToAdd = tagsToApply.filter(t => !origNames.includes(getTagName(t)));
      const tagsToRemoveList = originalTags.current.filter(t => !currNames.includes(getTagName(t)));

      if (tagsToAdd.length === 0 && tagsToRemoveList.length === 0) {
        if (onTagsApplied) onTagsApplied(tagsToApply);
        return;
      }

      setIsApplying(true);
      dispatch(bulkUpdateObjectTags({
        objectType, objectIds: selectedObjects, tagsToAdd, tagsToRemove: tagsToRemoveList, ...selectAllPayload,
        successCallback: () => {
          setIsApplying(false);
          if (onTagsApplied) onTagsApplied(tagsToApply);
        },
        failCallback: () => setIsApplying(false)
      }))
    }
  }

  // Filter tags based on search
  const filteredTags = allTags.filter(tag => {
    const tagName = typeof tag === 'string' ? tag : tag?.name || '';
    return tagName.toLowerCase().includes(searchText.toLowerCase());
  })

  return (
    <div className='tags-dropdown'>
      {createModalOpen ? (
        <>
          <div className='heading-area'>
            <h5>
              <button className='icon' onClick={() => setCreateModalOpen(false)}>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13L1 7L7 1" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
              Create New Tag
            </h5>
          </div>
          <div className='group-form'>
            <label>Tag Name</label>
            <TextField
              fullWidth
              value={newTagName}
              placeholder="Add tag name"
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => setNewTagName(e.target.value)}
            />
          </div>
          <h5>Choose a Color</h5>
          <div className='color-grid'>
            {colorPalette.map((color) => (
              <div className='color-box'
                key={color}
                onClick={() => setSelectedColor(color)}
                style={{
                  backgroundColor: color,
                  border: selectedColor === color ? '2px solid var(--primary-color)' : 'none',
                }}
              ></div>
            ))}
          </div>
          <button
            onClick={handleSaveNewTag}
            disabled={!newTagName.trim() || isApplying}
            className='btn-style-new mt-3 tags-action-btn'
          >
            {isApplying ? <> <CircularProgress size={16} /> Creating... </> : 'Create'}
          </button>
        </>
      ) : editModalOpen ? (
        <>
          <div className='heading-area'>
            <h5>
              <button className='icon' onClick={() => setEditModalOpen(false)}>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13L1 7L7 1" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
              Edit Tag
            </h5>
          </div>
          <div className='group-form'>
            <label>Tag Name</label>
            <TextField
              fullWidth
              value={editingTagName}
              placeholder="Add tag name"
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => setEditingTagName(e.target.value)}
            />
          </div>
          <h5>Choose a Color</h5>
          <div className='color-grid'>
            {colorPalette.map((color) => (
              <div className='color-box'
                key={color}
                onClick={() => setEditingTagColor(color)}
                style={{
                  backgroundColor: color,
                  border: editingTagColor === color ? '2px solid var(--primary-color)' : 'none',
                }}
              ></div>
            ))}
          </div>
          <button
            className='btn-style-new mt-3 tags-action-btn'
            onClick={() => {
              console.log('Button clicked directly!');
              handleUpdateTag();
            }}
            disabled={isApplying}
          >
            {isApplying ? (
              <>
                <CircularProgress size={16} />
                Updating...
              </>
            ) : (
              'Update'
            )}
          </button>
        </>
      ) : (<>
        <div className='heading-area'>
          {mode !== 'filter' && mode !== 'form' && (
            <h5>{mode === 'select' ? `Add Tags to ${effectiveCount} ${objectType}` : 'Manage Tags'}</h5>
          )}
          <div className='tags-header-right'>
            {mode === 'select' && tagsToApply.length > 0 && (
              <h5>{tagsToApply.length} tag{tagsToApply.length !== 1 ? 's' : ''} selected</h5>
            )}
            {onClose && (
              <IconButton size="small" className='tags-close-btn' onClick={onClose}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
            )}
          </div>
        </div>
        <div className='group-form' onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            size="small"
            value={searchText}
            placeholder="Search tags..."
            inputRef={searchInputRef}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        {/* Selected Tags Chips - cross buttons for quick removal */}
        {(mode === 'select' || mode === 'form') && tagsToApply.length > 0 && (
          <div className='selected-tags-chips'>
            {tagsToApply.map((tag, index) => {
              const tagName = typeof tag === 'string' ? tag : tag.name;
              const tagColor = typeof tag === 'string' ? '#10b981' : tag.color;
              return (
                <div className='tag-bottom-box' key={index} style={{ backgroundColor: tagColor }}>
                  {tagName}
                  <button className='btn-tag-cancel' onClick={() => handleTagSelection(tag)}>
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {/* Tags List */}
        <div className={`tag-list-area${mode === 'select' && isApplying ? ' tags-applying' : ''}`}>
          {mode === 'select' && isApplying && (
            <div className='tags-applying-overlay'>
              <CircularProgress size={20} className='tags-applying-spinner' />
            </div>
          )}
          {filteredTags.map((tag) => {
            const tagName = getTagName(tag);
            const tagColor = getTagColor(tag);
            const isAlreadyApplied = selectedTags.some(t => getTagName(t) === tagName);
            const isSelected = (mode === 'select' || mode === 'form')
              ? tagsToApply.some(t => getTagName(t) === tagName) || isAlreadyApplied
              : mode === 'filter'
                ? filterTags[tagName] || false
                : selectedTags.some(t => getTagName(t) === tagName);

            return (
              <div
                className={`taglist-box${mode === 'select' || mode === 'filter' || mode === 'form' ? ' taglist-selectable' : ''}${isSelected ? ' taglist-selected' : ''}`}
                key={tagName}
                style={{ backgroundColor: tagColor }}
                onClick={() => {
                  if (mode === 'select' || mode === 'form') handleTagSelection(tag);
                  else if (mode === 'filter') handleFilterTagToggle(tag);
                  else if (onTagSelect) onTagSelect(tag);
                }}
              >
                <div className='text-box'>
                  {mode === 'filter' && (
                    <Checkbox
                      checked={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterTagToggle(tag);
                      }}
                      size="small"
                    />
                  )}
                  <Typography className={mode === 'filter' ? 'filter-mode' : ''}>
                    {tagName}
                    {mode !== 'filter' && isSelected && (
                      <CheckIcon sx={{ fontSize: 14, color: '#fff', ml: '6px', verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                </div>

                <IconButton
                  size="small"
                  className='tag-menu-btn'
                  onClick={(e) => handleMenuClick(e, tag)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            );
          })}
        </div>
        {/* CREATE NEW TAG Button at bottom of list */}
        <button className='btn-style-border tags-create-btn' onClick={handleCreateTag}>
          Create New Tag
        </button>

        {/* Empty State */}
        {filteredTags.length === 0 && (
          <div className='no-tags-found'>
            <h5>No tags found</h5>
            <p>{searchText ? 'Try adjusting your search' : 'Create your first tag to get started'}</p>
          </div>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditTag}>
            <EditIcon className='edit-menu-icon' />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteTag} className='delete-menu-item'>
            <DeleteIcon className='edit-menu-icon' />
            {mode === 'select' && selectedObjects?.length > 0
              ? `Remove from ${effectiveCount} Selected`
              : 'Delete'}
          </MenuItem>
        </Menu>
      </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        className='delete-confirm-dialog'
      >
        <DialogTitle>
          {mode === 'select' && selectedObjects?.length > 0
            ? 'Remove Tag from Selected Items'
            : mode === 'filter'
              ? 'Delete Tag Globally'
              : 'Delete Tag'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to
            {mode === 'filter'
              ? ` delete the tag "${getTagName(selectedTag)}" from all ${objectType}?`
              : mode === 'select' && selectedObjects?.length > 0
                ? ` remove the tag "${getTagName(selectedTag)}" from ${effectiveCount} selected ${objectType}?`
                : ` remove the tag "${getTagName(selectedTag)}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <button className='btn-style-border' onClick={() => setDeleteConfirmOpen(false)} disabled={isApplying}>Cancel</button>
          <button
            className='btn-style-new delete-confirm-btn'
            onClick={handleConfirmDelete}
            disabled={isApplying}
          >
            {isApplying ? (
              <>
                <CircularProgress size={16} />
                {mode === 'select' && selectedObjects?.length > 0 ? 'Removing...' : 'Deleting...'}
              </>
            ) : (
              mode === 'select' && selectedObjects?.length > 0 ? 'Remove' : 'Delete'
            )}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TagsManager;
